import { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Download, Upload, FileText, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';

import { downloadExport, importRecords, type ImportResult } from '@/api/importExport';
import { Spinner } from '@/components/feedback/Spinner';

type Format = 'csv' | 'json';

export function ImportExportPage() {
  const { t } = useTranslation();

  // Export state
  const [exportFormat, setExportFormat] = useState<Format>('csv');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Import state
  const [importFormat, setImportFormat] = useState<Format>('csv');
  const [importMode, setImportMode] = useState<'merge' | 'replace'>('merge');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const exportMutation = useMutation({
    mutationFn: () => downloadExport({ format: exportFormat, from: from || undefined, to: to || undefined }),
    onError: (err: Error) => toast.error(err.message),
  });

  const importMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file selected');
      return importRecords(file, importMode, importFormat);
    },
    onSuccess: (data) => {
      setResult(data);
      if (data.errors.length === 0) {
        toast.success(
          t('importExport.importSuccess', { imported: data.imported })
        );
      } else {
        toast.warning(
          t('importExport.importPartial', { imported: data.imported, errors: data.errors.length })
        );
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const onPickFile = useCallback((f: File | null) => {
    setResult(null);
    if (!f) {
      setFile(null);
      return;
    }
    // Auto-detect format from extension
    const lower = f.name.toLowerCase();
    if (lower.endsWith('.json')) setImportFormat('json');
    else if (lower.endsWith('.csv')) setImportFormat('csv');
    setFile(f);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files?.[0];
      if (f) onPickFile(f);
    },
    [onPickFile]
  );

  const fileSizeLabel = useMemo(() => {
    if (!file) return '';
    const bytes = file.size;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }, [file]);

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
        {t('nav.importExport')}
      </h1>

      {/* EXPORT */}
      <section className="card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Download className="h-5 w-5 text-brand-600" aria-hidden />
          <h2 className="font-semibold">{t('importExport.exportTitle')}</h2>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('importExport.exportHelp')}
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="block text-xs font-medium mb-1">{t('importExport.format')}</label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as Format)}
              className="input"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">{t('records.filters.from')}</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">{t('records.filters.to')}</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="input"
            />
          </div>
        </div>

        <button
          onClick={() => exportMutation.mutate()}
          className="btn-primary"
          disabled={exportMutation.isPending}
        >
          {exportMutation.isPending ? <Spinner size="sm" /> : <Download className="h-4 w-4" />}
          {t('importExport.exportButton')}
        </button>
      </section>

      {/* IMPORT */}
      <section className="card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Upload className="h-5 w-5 text-brand-600" aria-hidden />
          <h2 className="font-semibold">{t('importExport.importTitle')}</h2>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('importExport.importHelp')}
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium mb-1">{t('importExport.format')}</label>
            <select
              value={importFormat}
              onChange={(e) => setImportFormat(e.target.value as Format)}
              className="input"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">{t('importExport.mode')}</label>
            <select
              value={importMode}
              onChange={(e) => setImportMode(e.target.value as 'merge' | 'replace')}
              className="input"
            >
              <option value="merge">{t('importExport.modeMerge')}</option>
              <option value="replace">{t('importExport.modeReplace')}</option>
            </select>
          </div>
        </div>

        {importMode === 'replace' && (
          <div className="rounded-md border border-rose-300 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{t('importExport.replaceWarning')}</span>
          </div>
        )}

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-label="Choose file"
          className={[
            'rounded-lg border-2 border-dashed px-6 py-10 text-center cursor-pointer transition',
            dragOver
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/30'
              : 'border-slate-300 dark:border-slate-700 hover:border-brand-400 hover:bg-slate-50 dark:hover:bg-slate-800/40',
          ].join(' ')}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={importFormat === 'csv' ? '.csv,text/csv' : '.json,application/json'}
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="flex items-center justify-center gap-3 text-sm">
              <FileText className="h-5 w-5 text-brand-600" />
              <span className="font-medium">{file.name}</span>
              <span className="text-slate-500">({fileSizeLabel})</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  setResult(null);
                }}
                className="text-slate-500 hover:text-rose-500 ml-2"
                aria-label={t('common.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="text-sm text-slate-600 dark:text-slate-400">
              <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <p>{t('importExport.dropzone')}</p>
              <p className="text-xs text-slate-500 mt-1">{t('importExport.dropzoneHint')}</p>
            </div>
          )}
        </div>

        <button
          onClick={() => importMutation.mutate()}
          className="btn-primary"
          disabled={!file || importMutation.isPending}
        >
          {importMutation.isPending ? <Spinner size="sm" /> : <Upload className="h-4 w-4" />}
          {t('importExport.importButton')}
        </button>

        {result && (
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              {result.errors.length === 0 ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              )}
              <span>
                {t('importExport.resultSummary', {
                  valid: result.valid,
                  imported: result.imported,
                  errors: result.errors.length,
                  mode: result.mode,
                })}
              </span>
            </div>
            {result.errors.length > 0 && (
              <div className="text-xs">
                <p className="font-medium text-rose-700 dark:text-rose-300 mb-1">
                  {t('importExport.errorsTitle')}
                </p>
                <ul className="space-y-1 max-h-48 overflow-y-auto">
                  {result.errors.slice(0, 50).map((e, idx) => (
                    <li key={idx} className="text-slate-600 dark:text-slate-400">
                      <span className="tabular-nums font-mono mr-2">L{e.line}</span>
                      <span className="font-mono text-slate-500 mr-2">{e.code}</span>
                      {e.message}
                    </li>
                  ))}
                  {result.errors.length > 50 && (
                    <li className="text-slate-500 italic">
                      + {result.errors.length - 50} more
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
